import type { VisualizationDTO } from "@zplanem/responses"
import { useState } from "react"
import {
	useVisualizations,
	useVisualizationsRealtime,
} from "../../hooks/use-visualizations"
import { getTranslations } from "../../i18n"
import { useLocale } from "../../locale-context"
import { modalNeighbors } from "./media-modal-nav"
import { useProjectDetailContext } from "./project-detail-context"
import { ProjectMediaAddModal } from "./project-media-add-modal"
import { VisualizationCard } from "./visualization-card"
import { VisualizationModal } from "./visualization-modal"

function VisualizationsIcon() {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.5"
			strokeLinecap="round"
			strokeLinejoin="round"
			className="w-10 h-10"
			aria-hidden="true"
		>
			<rect x="3" y="4" width="18" height="14" rx="2" />
			<path d="m3 14 4-4 4 4 3-3 4 4" />
			<circle cx="9" cy="9" r="1.2" />
		</svg>
	)
}

function PlusIcon() {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.5"
			strokeLinecap="round"
			className="w-5 h-5"
			aria-hidden="true"
		>
			<path d="M12 5v14M5 12h14" />
		</svg>
	)
}

/**
 * Visualizations & Plans sub-tab (ZPL-134/138/270): a responsive 1–5 column
 * grid of thumbnail cards. The add control is a small right-aligned button
 * above the grid when there are items (documents pattern, ZPL-270), and a
 * centered borderless button inside the empty-state box when there are none.
 * Clicking a card opens a url / pdf in a new tab, or an image/video in the
 * modal. The list refetches live via realtime.
 */
export function ProjectVisualizationsTab() {
	const { locale } = useLocale()
	const t = getTranslations(locale)
	const { propertyId, project } = useProjectDetailContext()
	const { data, isLoading, isError } = useVisualizations(propertyId, project.id)
	useVisualizationsRealtime(propertyId, project.id)
	const [uploadOpen, setUploadOpen] = useState(false)
	const [active, setActive] = useState<VisualizationDTO | null>(null)

	if (isLoading) {
		return (
			<div className="py-12 text-center text-sm text-muted-foreground">
				{t.common.loading}
			</div>
		)
	}
	// Not while the add modal is open: each confirmed upload fires a realtime
	// `visualization.changed` that refetches the list, and one failed background
	// refetch would otherwise unmount the modal mid-batch, destroying the
	// per-row done/failed state and the retry affordance. react-query keeps
	// the previous data, so the grid stays usable.
	if (isError && !uploadOpen) {
		return (
			<div className="py-12 text-center text-sm text-destructive">
				{t.projects.visualizations.errorLoad}
			</div>
		)
	}

	const items = data ?? []
	// ZPL-267: prev/next cycles only through modal-viewable items (image/video).
	const activeNeighbors = active ? modalNeighbors(items, active.id) : null

	function openVisualization(viz: VisualizationDTO) {
		if (viz.kind === "url" && viz.url) {
			window.open(viz.url, "_blank", "noopener,noreferrer")
			return
		}
		// PDFs open in a new tab; images/videos open in the modal.
		if (viz.kind === "pdf" && viz.sourceUrl) {
			window.open(viz.sourceUrl, "_blank", "noopener,noreferrer")
			return
		}
		setActive(viz)
	}

	return (
		<div className="space-y-4">
			{items.length === 0 ? (
				<div className="rounded-xl border border-dashed border-border bg-muted/30 px-6 py-16 flex flex-col items-center text-center">
					<div className="text-muted-foreground/70 mb-3">
						<VisualizationsIcon />
					</div>
					<h2 className="text-base font-bold text-foreground">
						{t.projects.visualizations.emptyTitle}
					</h2>
					<p className="mt-1.5 text-sm text-muted-foreground/80 max-w-sm">
						{t.projects.visualizations.emptyBody}
					</p>
					<button
						type="button"
						onClick={() => setUploadOpen(true)}
						className="mt-6 inline-flex cursor-pointer items-center gap-2 text-sm font-medium text-foreground/80 hover:text-foreground transition-colors"
					>
						<PlusIcon />
						{t.projects.visualizations.addCta}
					</button>
				</div>
			) : (
				<>
					<div className="flex items-center justify-end">
						<button
							type="button"
							onClick={() => setUploadOpen(true)}
							className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground/80 hover:text-foreground transition-colors"
						>
							<PlusIcon />
							{t.projects.visualizations.addCta}
						</button>
					</div>
					<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
						{items.map((viz) => (
							<VisualizationCard
								key={viz.id}
								visualization={viz}
								propertyId={propertyId}
								projectId={project.id}
								onOpen={() => openVisualization(viz)}
							/>
						))}
					</div>
				</>
			)}

			{uploadOpen ? (
				<ProjectMediaAddModal
					collection="visualization"
					propertyId={propertyId}
					projectId={project.id}
					existingCount={items.length}
					onClose={() => setUploadOpen(false)}
				/>
			) : null}
			{active ? (
				<VisualizationModal
					// Remount per item so the modal's local edit state resets when
					// navigating (ZPL-267).
					key={active.id}
					visualization={active}
					propertyId={propertyId}
					projectId={project.id}
					onClose={() => setActive(null)}
					onPrev={
						activeNeighbors ? () => setActive(activeNeighbors.prev) : undefined
					}
					onNext={
						activeNeighbors ? () => setActive(activeNeighbors.next) : undefined
					}
				/>
			) : null}
		</div>
	)
}
